import { DotsThree, FlowArrow, Plus, SquaresFour } from '@phosphor-icons/react';
import {
  getListFlowsQueryKey,
  useCreateFlow,
  useDeleteFlow,
  useListFlows,
} from '@reelplicate/api-client/flows';
import type {
  CreateFlowDto,
  FlowRunSummaryDto,
  FlowSummaryDto,
} from '@reelplicate/api-client/model';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  CATEGORIES,
  CATEGORY_LABELS,
  FLOW_TEMPLATES,
  getNodeDefinition,
  NODE_TYPES,
} from '@reelplicate/flows';
import {
  Badge,
  Button,
  CATEGORY_TINT,
  cn,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  Drawer,
  DrawerContent,
  EmptyState,
  FLOW_TINTS,
  IconButton,
  LibraryNode,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Skeleton,
  Stack,
  TemplateCard,
  toast,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';

import { formatWhen } from '@/lib/format-when';

import { ioLabel, nodeIcon } from './catalogue-ui';
import { runKind, STATUS_BADGE } from './flow-status';

/** `/flows` - templates to start from, and the workspace's flows. */
export function FlowsIndexPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: workspaces } = useListWorkspaces();
  const workspace = workspaces?.[0];
  const workspaceId = workspace?.id ?? '';
  const { data: flows } = useListFlows(workspaceId, {
    query: { enabled: Boolean(workspace) },
  });
  const create = useCreateFlow();
  const remove = useDeleteFlow();
  const [browsing, setBrowsing] = useState(false);
  const [deleting, setDeleting] = useState<FlowSummaryDto | null>(null);

  const start = async (data: CreateFlowDto) => {
    try {
      const flow = await create.mutateAsync({ id: workspaceId, data });
      await queryClient.invalidateQueries({
        queryKey: getListFlowsQueryKey(workspaceId),
      });
      void navigate(`/flows/${flow.id}`);
    } catch {
      return; // The failure is already shown as a toast.
    }
  };

  const deleteFlow = async (flow: FlowSummaryDto) => {
    try {
      await remove.mutateAsync({ id: workspaceId, flowId: flow.id });
    } catch {
      return;
    }
    await queryClient.invalidateQueries({ queryKey: getListFlowsQueryKey(workspaceId) });
    toast.success(`${flow.name} deleted`);
  };

  const columns: DataTableColumn<FlowSummaryDto>[] = [
    {
      id: 'flow',
      header: 'Flow',
      cell: (flow) => (
        // The whole cell opens the flow: icons, name and details.
        <RouterLink
          to={`/flows/${flow.id}`}
          aria-label={flow.name}
          className="group focus-visible:shadow-focus-ring flex min-w-0 items-center gap-2.5 rounded-xs outline-none"
        >
          <NodeStrip types={flow.nodeTypes} />
          <span className="flex min-w-0 flex-col">
            <span className="text-body-md-strong text-text-primary truncate group-hover:underline">
              {flow.name}
            </span>
            <span className="text-caption text-text-secondary">
              {workspace?.name} · {flow.nodeCount}{' '}
              {flow.nodeCount === 1 ? 'node' : 'nodes'}
            </span>
          </span>
        </RouterLink>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: 130,
      cell: (flow) => {
        const badge = STATUS_BADGE[flow.status];
        return (
          <Badge tone={badge.tone} dot={flow.status !== 'DRAFT'}>
            {badge.label}
          </Badge>
        );
      },
    },
    {
      id: 'trigger',
      header: 'Trigger',
      width: 160,
      cell: (flow) => (
        <Stack as="span" gap={0}>
          {trigger(flow)}
          {flow.nextRunAt && (
            <span className="text-caption text-text-secondary">
              Next {formatWhen(flow.nextRunAt)}
            </span>
          )}
        </Stack>
      ),
    },
    {
      id: 'lastRun',
      header: 'Last run',
      width: 140,
      cell: (flow) => (flow.lastRun ? whenLabel(flow.lastRun.startedAt) : '—'),
    },
    {
      id: 'result',
      header: 'Result',
      width: 220,
      cell: (flow) =>
        flow.lastRun ? (
          <span className="text-text-secondary">{runLabel(flow.lastRun)}</span>
        ) : (
          <span className="text-text-tertiary">Not run yet</span>
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      width: 56,
      cell: (flow) => (
        <Menu>
          <MenuTrigger asChild>
            <IconButton icon={DotsThree} size="sm" aria-label={`More for ${flow.name}`} />
          </MenuTrigger>
          <MenuContent align="end">
            <MenuItem onSelect={() => void navigate(`/flows/${flow.id}`)}>Open</MenuItem>
            <MenuItem type="danger" onSelect={() => setDeleting(flow)}>
              Delete flow
            </MenuItem>
          </MenuContent>
        </Menu>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-8 p-8">
      <header className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className="text-heading-h1 text-text-primary">Flows</h1>
          <p className="text-body-md text-text-secondary">
            Turn winners into variations - or run experiments until one wins.
          </p>
        </div>
        <Button
          variant="outline"
          leadingIcon={SquaresFour}
          onClick={() => setBrowsing(true)}
        >
          Browse nodes
        </Button>
        <Button
          leadingIcon={Plus}
          disabled={!workspace}
          loading={create.isPending && create.variables?.data.templateKey === undefined}
          onClick={() => void start({})}
        >
          New flow
        </Button>
      </header>

      <section aria-labelledby="templates-heading" className="flex flex-col gap-3">
        <h2 id="templates-heading" className="text-heading-h4 text-text-primary">
          Start from a template
        </h2>
        <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 desktop:grid-cols-4">
          {FLOW_TEMPLATES.map((template) => (
            <TemplateCard
              key={template.key}
              nodes={template.graph.nodes.slice(0, 4).map((node) => {
                const definition = getNodeDefinition(node.type)!;
                return { category: definition.category, icon: nodeIcon(definition) };
              })}
              title={template.name}
              description={template.description}
              meta={template.note}
              disabled={!workspace || create.isPending}
              onClick={() =>
                void start({ templateKey: template.key as CreateFlowDto['templateKey'] })
              }
            />
          ))}
        </div>
      </section>

      <section aria-labelledby="flows-heading" className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <h2 id="flows-heading" className="text-heading-h4 text-text-primary">
            Your flows
          </h2>
          {flows && <Badge>{flows.length}</Badge>}
        </div>
        {!flows ? (
          <Skeleton className="h-40" shape="rectangle" />
        ) : (
          <DataTable
            label="Your flows"
            columns={columns}
            rows={flows}
            getRowId={(flow) => flow.id}
            getRowLabel={(flow) => flow.name}
            empty={
              <EmptyState
                icon={FlowArrow}
                title="No flows yet"
                description="Start from a template above, or build one from scratch."
                actions={
                  <Button
                    variant="outline"
                    leadingIcon={Plus}
                    onClick={() => void start({})}
                  >
                    New flow
                  </Button>
                }
              />
            }
          />
        )}
      </section>

      <NodeCatalogue open={browsing} onOpenChange={setBrowsing} />
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={`Delete ${deleting?.name ?? 'flow'}?`}
        description="The flow and its saved versions are removed. This can’t be undone."
        confirmLabel="Delete flow"
        onConfirm={() => deleting && void deleteFlow(deleting)}
      />
    </div>
  );
}

/** "Today 09:02", or "Tue 22 Sep". */
function whenLabel(iso: string): string {
  const date = new Date(iso);
  const today = new Date().toDateString() === date.toDateString();
  return today
    ? `Today ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : date.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
}

function runLabel(run: FlowRunSummaryDto): string {
  const kind = runKind(run);
  switch (run.status) {
    case 'QUEUED':
    case 'RUNNING':
      return `${kind} in progress`;
    case 'WAITING':
      return 'Waiting for review';
    case 'SLEEPING':
      return 'Measuring';
    case 'SUCCEEDED':
      return `${kind} done`;
    case 'FAILED':
      return `${kind} failed`;
    case 'CANCELLED':
      return `${kind} stopped`;
  }
}

function trigger(flow: FlowSummaryDto): string {
  if (flow.nodeTypes.includes('starter.schedule')) return 'On a schedule';
  if (flow.nodeTypes.includes('starter.webhook')) return 'Webhook';
  if (flow.runsAgainOnWinner) return 'On Pick winner';
  return 'Manual';
}

/** The row's first three node icons, overlapped (Figma "Mini" strip). */
function NodeStrip({ types }: { types: string[] }) {
  return (
    <span aria-hidden className="flex shrink-0">
      {types.slice(0, 3).map((type) => {
        const definition = getNodeDefinition(type);
        const Icon = nodeIcon(definition);
        const tint = FLOW_TINTS[CATEGORY_TINT[definition?.category ?? 'starter']];
        return (
          <span
            key={type}
            className={cn(
              'bg-bg-surface -mr-1 flex size-6.5 items-center justify-center rounded-md inset-ring-[1.5px] last:mr-0',
              tint.ring,
            )}
          >
            <Icon className={cn('size-3.5', tint.text)} />
          </span>
        );
      })}
    </span>
  );
}

/** "Browse nodes": the whole catalogue with what each node takes and hands on. */
function NodeCatalogue({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        size="wide"
        title="Node library"
        description="Every node, what it takes in and what it hands on. Only compatible types connect."
      >
        <Stack gap={6}>
          {CATEGORIES.map((category) => (
            <section key={category} aria-label={CATEGORY_LABELS[category]}>
              <h3 className="text-overline text-text-tertiary mb-1 uppercase">
                {CATEGORY_LABELS[category]}
              </h3>
              {NODE_TYPES.filter((node) => node.category === category).map((node) => (
                <LibraryNode
                  key={node.type}
                  category={category}
                  icon={nodeIcon(node)}
                  title={node.label}
                  description={node.description}
                  io={ioLabel(node)}
                  soon={node.status === 'soon'}
                />
              ))}
            </section>
          ))}
        </Stack>
      </DrawerContent>
    </Drawer>
  );
}
